import React, { useMemo, useRef, useEffect } from 'react';

const UniversalChatDock = ({
  title = 'Universal Chat',
  collapsed = true,
  onToggleCollapsed,
  targets = [],
  selectedTargetId = '',
  onSelectTarget,
  participants = [],
  messages = [],
  currentUserId = '',
  inputValue = '',
  onInputChange,
  onSend,
  placeholder = 'Type a message...',
  unreadCount = 0,
  unreadByTargetId = {},
  unreadNotices = [],
  onNoticeClick,
  onReplyToSender,
  canDeleteMessages = false,
  onDeleteMessage,
  emptyState = 'No messages for this target yet.',
  voiceControls = null,
}) => {
  const messagesRef = useRef(null);

  const selectedTarget = useMemo(
    () => targets.find((target) => target.id === selectedTargetId) || targets[0] || null,
    [targets, selectedTargetId]
  );
  const isPrivateTarget = selectedTarget?.type === 'dm';
  const isPrivateConnected = !!voiceControls?.isJoined && isPrivateTarget && (voiceControls?.remoteCount || 0) > 0;

  useEffect(() => {
    if (!messagesRef.current) return;
    messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
  }, [messages.length, selectedTargetId, collapsed]);

  return (
    <section className={`universal-chat-shell ${collapsed ? 'collapsed' : ''}`} aria-label="Universal chat">
      <div className="universal-chat-head">
        <div className="universal-chat-head-title">
          <strong>{title}</strong>
          <span className="universal-chat-current-target">{selectedTarget?.label || 'No target selected'}</span>
          {unreadCount > 0 && <span className="universal-chat-unread">{unreadCount}</span>}
        </div>
        <button type="button" className="thin-header-btn" onClick={onToggleCollapsed}>
          {collapsed ? 'Expand' : 'Collapse'}
        </button>
      </div>

      {!collapsed && (
        <>
          <div className="universal-chat-controls">
            <select value={selectedTargetId} onChange={(e) => onSelectTarget?.(e.target.value)} aria-label="Choose chat target">
              {targets.map((target) => (
                <option key={target.id} value={target.id}>
                  {target.label}{(unreadByTargetId?.[target.id] || 0) > 0 ? ` (${unreadByTargetId[target.id]})` : ''}
                </option>
              ))}
            </select>
          </div>

          {voiceControls?.enabled && (
            <div className="universal-chat-notices" aria-label="Voice chat controls">
              <span className="admin-note">
                Active Pipe: {voiceControls?.activePipeLabel || 'Disconnected'}
              </span>
              <span className="admin-note">
                Selected Pipe: {voiceControls?.selectedPipeLabel || 'Room Pipe'}
              </span>
              {!voiceControls?.isJoined && !isPrivateTarget && (
                <button
                  type="button"
                  className="thin-header-btn"
                  onClick={voiceControls?.onJoinSelectedPipe || voiceControls?.onJoin}
                >
                  Join Selected Pipe
                </button>
              )}
              {!!voiceControls?.isJoined && !isPrivateTarget && (
                <button
                  type="button"
                  className="thin-header-btn"
                  onClick={voiceControls?.onJoinSelectedPipe || voiceControls?.onJoin}
                >
                  Switch To Selected Pipe
                </button>
              )}
              {voiceControls?.isJoined && (
                <>
                  <button type="button" className="thin-header-btn" onClick={voiceControls?.onLeave}>
                    Leave Voice
                  </button>
                  <button type="button" className="thin-header-btn" onClick={voiceControls?.onToggleMute}>
                    {voiceControls?.isMuted ? 'Unmute' : 'Mute'}
                  </button>
                </>
              )}
              {isPrivateTarget && !isPrivateConnected && (
                <button
                  type="button"
                  className="thin-header-btn"
                  onClick={() => voiceControls?.onStartPrivateCall?.(selectedTarget)}
                >
                  Call {selectedTarget?.recipientName || 'Player'}
                </button>
              )}
              {isPrivateConnected && (
                <span className="admin-note">
                  Private pipe connected with {voiceControls?.remoteCount || 0} participant{(voiceControls?.remoteCount || 0) === 1 ? '' : 's'}
                </span>
              )}
              {!isPrivateTarget && voiceControls?.isJoined && (
                <span className="admin-note">
                  Voice active with {voiceControls?.remoteCount || 0} participant{(voiceControls?.remoteCount || 0) === 1 ? '' : 's'}
                </span>
              )}
              {!!voiceControls?.error && <span className="error">{voiceControls.error}</span>}
            </div>
          )}

          {voiceControls?.enabled && voiceControls?.incomingCall && (
            <div className="universal-chat-notices" aria-label="Incoming voice call">
              <span className="admin-note">
                Incoming call from {voiceControls.incomingCall.fromDisplayName || 'Player'}
              </span>
              <button type="button" className="thin-header-btn" onClick={voiceControls?.onAcceptIncomingCall}>
                Accept
              </button>
              <button type="button" className="thin-header-btn" onClick={voiceControls?.onDeclineIncomingCall}>
                Decline
              </button>
            </div>
          )}

          {unreadNotices.length > 0 && (
            <div className="universal-chat-notices" aria-label="Unread chat notices">
              {unreadNotices.map((notice) => (
                <button
                  key={`notice-${notice.targetId}`}
                  type="button"
                  className="thin-header-btn"
                  onClick={() => onNoticeClick?.(notice.targetId)}
                >
                  {notice.label}: {notice.count}
                </button>
              ))}
            </div>
          )}

          {onReplyToSender && (
            <div className="universal-chat-notices" aria-label="Direct message hint">
              {selectedTarget?.type === 'dm' ? (
                <span className="admin-note">
                  DM selected: type below to message {selectedTarget?.recipientName || 'this user'}.
                </span>
              ) : (
                <span className="admin-note">
                  Tip: click DM on a message to switch this chat to a private DM thread.
                </span>
              )}
            </div>
          )}

          {participants.length > 0 && (
            <div className="universal-chat-participants">
              {participants.map((entry) => (
                <span key={`universal-chat-chip-${entry.userId}`} className={`leader-activity-badge ${entry.isOnline ? 'online' : 'offline'}`}>
                  {entry.displayName}
                </span>
              ))}
            </div>
          )}

          <div className="universal-chat-messages" ref={messagesRef}>
            {messages.length > 0 ? (
              messages.map((msg, idx) => {
                const own = `${msg?.senderId || ''}` === `${currentUserId || ''}`;
                return (
                  <div key={`${msg?.id || idx}-${idx}`} className={`game-chat-message ${own ? 'own' : ''}`}>
                    <span className="game-chat-name">{own ? 'You' : msg?.senderName || 'Player'}</span>
                    <span className="game-chat-text">{msg?.body || ''}</span>
                    {!own && onReplyToSender && (
                      <button
                        type="button"
                        className="thin-header-btn"
                        title="Switch to DM with this user"
                        aria-label="Open direct message"
                        onClick={() => onReplyToSender(msg)}
                      >
                        DM
                      </button>
                    )}
                    {canDeleteMessages && !!msg?.id && onDeleteMessage && (
                      <button
                        type="button"
                        className="thin-header-btn"
                        onClick={() => onDeleteMessage(msg)}
                      >
                        Delete
                      </button>
                    )}
                  </div>
                );
              })
            ) : (
              <p className="game-chat-empty">{emptyState}</p>
            )}
          </div>

          <form className="universal-chat-input-row" onSubmit={onSend}>
            <input
              type="text"
              maxLength={400}
              value={inputValue}
              placeholder={placeholder}
              onChange={(e) => onInputChange?.(e.target.value)}
              disabled={selectedTarget?.type === 'dm_inbox'}
            />
            <button type="submit" className="btn-play" disabled={selectedTarget?.type === 'dm_inbox'}>Send</button>
          </form>
        </>
      )}
    </section>
  );
};

export default UniversalChatDock;
